import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { CityProvider } from "@/contexts/CityContext";
import { ChatBubble } from "@/components/chat/ChatBubble";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { StickyMobileCTA } from "@/components/layout/StickyMobileCTA";

// Eager load critical pages
import Index from "./pages/Index";
import Search from "./pages/Search";
import Property from "./pages/Property";

// Lazy load non-critical pages for better initial load
const Auth = lazy(() => import("./pages/Auth"));
const NeighborhoodPage = lazy(() => import("./pages/NeighborhoodPage"));
const Map = lazy(() => import("./pages/Map"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

// City landing pages
const EdmontonHomes = lazy(() => import("./pages/EdmontonHomes"));
const CalgaryHomes = lazy(() => import("./pages/CalgaryHomes"));

// Edmonton hub pages
const SouthEdmontonHomes = lazy(() => import("./pages/edmonton/SouthEdmontonHomes"));
const SouthwestEdmontonHomes = lazy(() => import("./pages/edmonton/SouthwestEdmontonHomes"));
const SoutheastEdmontonHomes = lazy(() => import("./pages/edmonton/SoutheastEdmontonHomes"));
const EdmontonCondos = lazy(() => import("./pages/edmonton/EdmontonCondos"));

// Top Ranked pages
const TopRanked = lazy(() => import("./pages/TopRanked"));
const Community = lazy(() => import("./pages/Community"));

// Market Stats page
const MarketStats = lazy(() => import("./pages/MarketStats"));

// Viewing Plan pages
const ViewingPlan = lazy(() => import("./pages/ViewingPlan"));
const ViewingPlanThanks = lazy(() => import("./pages/ViewingPlanThanks"));

// Lazy load dashboard pages
const DashboardLayout = lazy(() => import("./pages/dashboard/DashboardLayout"));
const Favorites = lazy(() => import("./pages/dashboard/Favorites"));
const SavedSearches = lazy(() => import("./pages/dashboard/SavedSearches"));
const History = lazy(() => import("./pages/dashboard/History"));
const Inquiries = lazy(() => import("./pages/dashboard/Inquiries"));

// Lazy load admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Leads = lazy(() => import("./pages/admin/Leads"));
const Agents = lazy(() => import("./pages/admin/Agents"));
const UserRoles = lazy(() => import("./pages/admin/UserRoles"));
const Properties = lazy(() => import("./pages/admin/Properties"));
const Users = lazy(() => import("./pages/admin/Users"));
const Announcements = lazy(() => import("./pages/admin/Announcements"));
const NewsManagement = lazy(() => import("./pages/admin/NewsManagement"));
const ErrorLogs = lazy(() => import("./pages/admin/ErrorLogs"));
const PropertyAds = lazy(() => import("./pages/admin/PropertyAds"));
const SEO = lazy(() => import("./pages/admin/SEO"));
const SiteHealth = lazy(() => import("./pages/admin/SiteHealth"));
const Rankings = lazy(() => import("./pages/admin/Rankings"));
const IdxTest = lazy(() => import("./pages/admin/IdxTest"));
const AdminSystemMap = lazy(() => import("./pages/admin/SystemMap"));
const ViewingPlanSubmissions = lazy(() => import("./pages/admin/ViewingPlanSubmissions"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // Data stays fresh for 2 minutes
      gcTime: 1000 * 60 * 10,   // Cache kept for 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/search" element={<Search />} />
        <Route path="/properties" element={<Navigate to="/search" replace />} />
        <Route path="/listings" element={<Navigate to="/search" replace />} />
        <Route path="/homes-for-sale" element={<Navigate to="/search" replace />} />
        <Route path="/map" element={<Map />} />
        <Route path="/property/:id" element={<Property />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:id" element={<NewsArticle />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        {/* City landing pages */}
        <Route path="/edmonton" element={<EdmontonHomes />} />
        <Route path="/calgary" element={<CalgaryHomes />} />
        {/* Edmonton hub pages */}
        <Route path="/edmonton/south-edmonton-homes-for-sale" element={<SouthEdmontonHomes />} />
        <Route path="/edmonton/southwest-edmonton-homes-for-sale" element={<SouthwestEdmontonHomes />} />
        <Route path="/edmonton/southeast-edmonton-homes-for-sale" element={<SoutheastEdmontonHomes />} />
        <Route path="/edmonton/condos-for-sale" element={<EdmontonCondos />} />
        {/* Dynamic neighborhood landing pages */}
        <Route path="/edmonton/:slug" element={<NeighborhoodPage city="Edmonton" />} />
        <Route path="/calgary/:slug" element={<NeighborhoodPage city="Calgary" />} />
        {/* Top Ranked pages */}
        <Route path="/top-ranked" element={<TopRanked />} />
        <Route path="/community/:slug" element={<Community />} />
        {/* Market Stats page */}
        <Route path="/market-stats" element={<MarketStats />} />
        {/* Viewing Plan pages */}
        <Route path="/viewing-plan" element={<ViewingPlan />} />
        <Route path="/viewing-plan/thanks" element={<ViewingPlanThanks />} />
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<Navigate to="/dashboard/favorites" replace />} />
          <Route path="favorites" element={<Favorites />} />
          <Route path="searches" element={<SavedSearches />} />
          <Route path="history" element={<History />} />
          <Route path="inquiries" element={<Inquiries />} />
        </Route>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/leads" replace />} />
          <Route path="leads" element={<Leads />} />
          <Route path="viewing-plans" element={<ViewingPlanSubmissions />} />
          <Route path="agents" element={<Agents />} />
          <Route path="roles" element={<UserRoles />} />
          <Route path="properties" element={<Properties />} />
          <Route path="users" element={<Users />} />
          <Route path="announcements" element={<Announcements />} />
          <Route path="news" element={<NewsManagement />} />
          
          <Route path="errors" element={<ErrorLogs />} />
          <Route path="ads" element={<PropertyAds />} />
          <Route path="seo" element={<SEO />} />
          <Route path="health" element={<SiteHealth />} />
          <Route path="rankings" element={<Rankings />} />
          <Route path="idx-test" element={<IdxTest />} />
          <Route path="system-map" element={<AdminSystemMap />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CityProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <div className="pb-28 md:pb-0">
              <AppRoutes />
            </div>
            <StickyMobileCTA />
            <MobileBottomNav />
            <ChatBubble />
          </BrowserRouter>
        </TooltipProvider>
      </CityProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;